/**
 * Public Auth Hook
 * 
 * Handles inline signup/login within the public chat.
 */

import { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import jwt from 'jsonwebtoken';
import isEmail from 'validator/lib/isEmail';
import { forgotPassword, getUserInfo } from 'API/user';
import { applyProfileLanguage } from 'hooks/use-profile-language';
import i18n from 'i18n';
import useUserStore from 'stores/user-store';
import useErrorStore from 'stores/error-store';
import type { InlineSignupRequest, InlineLoginRequest, AuthResponse } from '../public-chat.types';
import { Roles, ErrorMessages } from 'enums';
import { getPostAuthRedirectPath, getPostRegistrationRedirectPath } from 'utils/auth-redirect';

const API_BASE = '/api/v1/public-chat';

export type RequestPasswordResetResult =
  | 'success'
  | 'invalid_email'
  | 'user_not_found'
  | 'email_validation'
  | 'server';

interface UsePublicAuthReturn {
  isLoading: boolean;
  error: string | null;
  signup: (data: Omit<InlineSignupRequest, 'sessionId'>, sessionId: string) => Promise<boolean>;
  login: (data: Omit<InlineLoginRequest, 'sessionId'>, sessionId: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<RequestPasswordResetResult>;
  clearError: () => void;
}

export function usePublicAuth(): UsePublicAuthReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const signIn = useUserStore((state) => state.signIn);
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  // Handle signup
  const signup = useCallback(async (
    data: Omit<InlineSignupRequest, 'sessionId'>,
    sessionId: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          sessionId,
          // Die erkannte UI-Sprache (berücksichtigt ?lang= und eine frühere Wahl), nicht nur
          // navigator.language — sie wird zur Profilsprache und gilt nach dem Login.
          locale: (i18n.language || navigator.language || 'de').split('-')[0],
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          setError('An account with this email already exists. Please login instead.');
        } else {
          setError(result.error || 'Failed to create account');
        }
        return false;
      }

      const authResult = result as AuthResponse;

      // Store tokens
      localStorage.setItem('accessToken', authResult.token.accessToken);
      localStorage.setItem('refreshToken', authResult.token.refreshToken);

      // Decode the JWT token to extract the role
      const decoded: any = jwt.decode(authResult.token.accessToken, { complete: true });
      const role = decoded?.payload?.role || Roles.BASIC;

      // Update user store
      signIn(
        {
          _id: authResult.user.id,
          email: authResult.user.email,
          firstName: authResult.user.firstName,
          lastName: authResult.user.lastName,
          tenant: authResult.tenant.id,
          role: role,
        } as any,
        {
          id: authResult.user.id,
          email: authResult.user.email,
          // Sonst bleiben Vor-/Nachname im Profil leer, bis /users/me neu geladen wird.
          firstName: authResult.user.firstName ?? '',
          lastName: authResult.user.lastName ?? '',
        } as any,
        [],
        { accessToken: authResult.token.accessToken, refreshToken: authResult.token.refreshToken }
      );

      // Neukonto → immer in den AI-Chat, dort startet der Einrichtungs-Wizard.
      navigate(getPostRegistrationRedirectPath(), { replace: true });
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [location, navigate, signIn]);

  // Handle login
  const login = useCallback(async (
    data: Omit<InlineLoginRequest, 'sessionId'>,
    sessionId: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          // Only include sessionId if it's a valid non-empty string
          ...(sessionId ? { sessionId } : {}),
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          setError('Invalid email or password');
        } else {
          setError(result.error || 'Failed to login');
        }
        return false;
      }

      const authResult = result as AuthResponse;

      // Store tokens
      localStorage.setItem('accessToken', authResult.token.accessToken);
      localStorage.setItem('refreshToken', authResult.token.refreshToken);

      // Decode the JWT token to extract the role
      const decoded: any = jwt.decode(authResult.token.accessToken, { complete: true });
      const role = decoded?.payload?.role || Roles.BASIC;

      // Update user store
      signIn(
        {
          _id: authResult.user.id,
          email: authResult.user.email,
          firstName: authResult.user.firstName,
          lastName: authResult.user.lastName,
          tenant: authResult.tenant.id,
          role: role,
        } as any,
        {
          id: authResult.user.id,
          email: authResult.user.email,
          // Sonst bleiben Vor-/Nachname im Profil leer, bis /users/me neu geladen wird.
          firstName: authResult.user.firstName ?? '',
          lastName: authResult.user.lastName ?? '',
        } as any,
        [],
        { accessToken: authResult.token.accessToken, refreshToken: authResult.token.refreshToken }
      );

      // Sofort navigieren, im selben Takt wie signIn: App.tsx baut den Router neu, sobald
      // der Login im Store steht. Ein await davor (Profil laden) ließ navigate auf dem
      // alten, schon ersetzten Router laufen — man blieb auf der Login-Seite stehen.
      navigate(getPostAuthRedirectPath(location), { replace: true });

      // UI-Sprache aus dem Profil übernehmen (der Login-Response enthält sie nicht) —
      // nachgelagert, darf den Login nicht blockieren.
      void getUserInfo()
        .then((userInfo) => {
          if (userInfo && !userInfo.error) applyProfileLanguage(userInfo.language);
        })
        .catch(() => undefined);
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to login');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [location, navigate, signIn]);

  const requestPasswordReset = useCallback(
    async (email: string): Promise<RequestPasswordResetResult> => {
      const trimmed = email.trim();
      if (!trimmed || !isEmail(trimmed)) {
        return 'invalid_email';
      }
      setIsLoading(true);
      setError(null);
      try {
        const res = await forgotPassword({ email: trimmed });
        if (res && !(res as { error?: unknown }).error) {
          return 'success';
        }
        const err = (res as { error?: { code?: number; message?: string; errors?: { body?: { message?: string }[] } } })
          .error;
        const validationErrorMessage = err?.errors?.body?.[0]?.message;
        if (err?.code === 400 && err?.message === ErrorMessages.USER_NOT_EXISTS) {
          return 'user_not_found';
        }
        if (err?.code === 400 && validationErrorMessage === ErrorMessages.EMAIL_VALIDATION_ERROR) {
          return 'email_validation';
        }
        handleOpenErrorDialog(err as Parameters<typeof handleOpenErrorDialog>[0], '/auth/forgot-password', 'post');
        return 'server';
      } finally {
        setIsLoading(false);
      }
    },
    [handleOpenErrorDialog]
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    signup,
    login,
    requestPasswordReset,
    clearError,
  };
}

export default usePublicAuth;

